import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { images } from '../../components/shared/assets';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { useTranslation } from 'react-i18next';
import { createSelfbookHandler } from '../../utils/selfbook';
import './ExperiencesAtacama.css';

const ExperiencesAtacama: React.FC = () => {
  const navigate = useNavigate();
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();
  const handleBooking = createSelfbookHandler(navigate, '/atacama/contact/');

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, []);

  // Experience items data with approved content from content maps
  const experiences = [
    {
      id: 1,
      image: '/assets/figma/a61743d448b690cd99655fcd00304d8cc13c5625.png',
      title: 'Horseback Riding',
      location: 'AWASI ATACAMA',
      description: 'Explore the world\'s driest desert on horseback. This excursion takes you through some of Atacama\'s iconic landscapes: the Valle de Marte, the formations of la Cordillera de la Sal, or the historic Ayllus. The route will be selected based on your riding experience and personal preferences.'
    },
    {
      id: 2,
      image: '/assets/figma/4d4e790dd105e45b604aa440eb2541894dd317b3.png',
      title: 'Sunrise At Licancabur Volcano',
      location: 'AWASI ATACAMA',
      description: 'Experience the breathtaking sunrise from one of the most iconic volcanoes in the Atacama region, offering spectacular views across the high altitude landscapes and salt flats below.'
    },
    {
      id: 3,
      image: '/assets/figma/e4b1294f7fdb8bb50ef6b56bc1b1317911f04905.png',
      title: 'Astronomical Observation',
      location: 'AWASI ATACAMA',
      description: 'The Atacama Desert offers some of the clearest skies in the world for stargazing. Join our expert guides for an unforgettable journey through the cosmos and discover the wonders of the southern hemisphere sky.'
    },
    {
      id: 4,
      image: '/assets/figma/4f09b905d851cc5f7a6af53dc7a75c5a38f924e9.png',
      title: 'Desert Picnic Experience',
      location: 'AWASI ATACAMA',
      description: 'How about enjoying your meal surrounded by giant cacti? Our Awasi private guides are experts at creating unforgettable dining experiences set against pristine views.'
    },
    {
      id: 5,
      image: '/assets/figma/853014457bcaecdf6a163e42e820f91e22e032c8.png',
      title: 'Excursion to Laguna Chaxa in the Salar de Atacama',
      location: 'AWASI ATACAMA',
      description: 'Chaxa Lagoon is notable for its expansive views of the Atacama basin. The main attractions include the salt flat formations and a diverse variety of birdlife; particularly flamingos, which can be observed depending on the season.'
    },
    {
      id: 6,
      image: '/assets/figma/4068ccb307e6b17d16d0cec4e523eac819e02a87.png',
      title: 'Choose your pace',
      location: 'AWASI ATACAMA',
      description: 'You may choose between full-day excursions with lunch included (from 4 to 6 hrs.); or two half-day excursions (each one between 2 and 5 hrs.) returning to have lunch at Awasi after the first excursion and heading out again in the afternoon.'
    }
  ];

  // Team members data with translated content
  const teamMembers = [
    {
      id: 1,
      image: '/assets/figma/3ae52cc439b17491f4592b688d60787859659cb8.png',
      name: t('properties/atacama:experiences.team.members.carlosBarros.name'),
      role: t('properties/atacama:experiences.team.members.carlosBarros.role'),
      description: t('properties/atacama:experiences.team.members.carlosBarros.description')
    },
    {
      id: 2,
      image: '/assets/figma/d36ae0b49c529e7fed4f9034de295d3fe68cf1d4.png',
      name: t('properties/atacama:experiences.team.members.cristianLopez.name'),
      role: t('properties/atacama:experiences.team.members.cristianLopez.role'),
      description: t('properties/atacama:experiences.team.members.cristianLopez.description')
    },
    {
      id: 3,
      image: '/assets/figma/0900d5204eb6e5804f3a3f031befd03ac9cff02f.png',
      name: t('properties/atacama:experiences.team.members.paulaBertotto.name'),
      role: t('properties/atacama:experiences.team.members.paulaBertotto.role'),
      description: t('properties/atacama:experiences.team.members.paulaBertotto.description')
    },
    {
      id: 4,
      image: '/assets/figma/f8d36502f184423204876f08dda07784a2c30b3c.png',
      name: t('properties/atacama:experiences.team.members.franciscoSilva.name'),
      role: t('properties/atacama:experiences.team.members.franciscoSilva.role'),
      description: t('properties/atacama:experiences.team.members.franciscoSilva.description')
    },
    {
      id: 5,
      image: '/assets/figma/d53cfac2cff0fee3442e52c745b5b4b1d7398942.png',
      name: t('properties/atacama:experiences.team.members.franciscoSilva.name'),
      role: t('properties/atacama:experiences.team.members.franciscoSilva.role'),
      description: t('properties/atacama:experiences.team.members.franciscoSilva.description')
    }
  ];

  const showLinks = false;

  return (
    <div className="experiences-atacama">
      {/* Navigation - Atacama variant */}
      <UnifiedNavigation variant="light" destination="atacama" />

      {/* Text Block Header */}
      <section className="experiences-header-section" data-scroll-section>
        <div className="experiences-header-content">
          <h1>{t('properties/atacama:experiences.pageTitle')}</h1>
          <p>
            {t('properties/atacama:experiences.pageDescription')}
          </p>
        </div>
      </section>

      {/* Full Width Hero Image */}
      <section className="experiences-hero-image" data-scroll-section>
        <div
          className="hero-image-container"
          style={{ backgroundImage: `url('/assets/images/atacama-carousel/experience-background.png')` }}
        />
      </section>

      {/* Guide/Experiences Section - 3x2 Grid */}
      <section className="experiences-guide-section" data-scroll-section>
        <div className="guide-section-container">
          {/* Filters and Guide Button */}
          <div className="guide-section-header">
            <div className="filter-buttons">
              <button className="filter-button active">
                <span>{t('properties/atacama:experiences.filters.location')}</span>
              </button>
              <button className="filter-button">
                <span>{t('properties/atacama:experiences.filters.type')}</span>
              </button>
            </div>
            <button className="guide-button">
              <span>{t('properties/atacama:experiences.filters.ourGuides')}</span>
            </button>
          </div>

          <div className="experiences-grid">
            {experiences.map((experience) => (
              <div key={experience.id} className="experience-card">
                <div className="experience-image">
                  <img src={experience.image} alt={experience.title} />
                </div>
                <div className="experience-content">
                  <div className="experience-content-main">
                    <div className="experience-content-header">
                      <h3>{experience.title}</h3>
                      <div className="experience-location">
                        <span>ATACAMA</span>
                        <span>-</span>
                        <span>LOREM IPSUM</span>
                      </div>
                    </div>
                    <p>{experience.description}</p>
                  </div>
                  <div className="experience-content-actions">
                    <button className="contact-link" onClick={handleBooking}>
                      CONTACT US
                      <svg className="underline" viewBox="0 0 78 2" fill="none">
                        <line y1="1" x2="78" y2="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="guide-pagination">
            <button className="pagination-button active">{t('properties/atacama:experiences.pagination.page1')}</button>
            <button className="pagination-button">{t('properties/atacama:experiences.pagination.page2')}</button>
            <button className="pagination-button">{t('properties/atacama:experiences.pagination.page3')}</button>
            <button className="pagination-button">{t('properties/atacama:experiences.pagination.page4')}</button>
          </div>
        </div>
      </section>

      {/* Team Header + Body */}
      <section className="experiences-team-section" data-scroll-section>
        <div className="team-section-container">
          <div className="team-header">
            <h2>{t('properties/atacama:experiences.team.sectionTitle')}</h2>
            <p>
              {t('properties/atacama:experiences.team.sectionDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Carousel */}
      <section className="experiences-team-carousel" data-scroll-section>
        <div className="team-carousel-wrapper">
          <div className="team-carousel-container" id="team-carousel">
            <div className="team-members-scroll">
              {teamMembers.map((member) => (
                <div key={member.id} className="team-member-card">
                  <div className="member-image">
                    {member.image ? (
                      <img src={member.image} alt={member.name} />
                    ) : (
                      <div className="placeholder-image" />
                    )}
                  </div>
                  <div className="member-content">
                    <h3>{member.name}</h3>
                    <span className="member-role">{member.role}</span>
                    <p>{member.description}</p>
                    <button className="read-more-link">
                      {t('properties/atacama:experiences.team.readMore')}
                      <svg className="underline" viewBox="0 0 76 2" fill="none">
                        <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-nav-arrow" aria-label="Next">
              <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                <path d="M22 14L37 29L22 44" stroke="#49413d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Full Image */}
      <section className="experiences-cta-section" data-scroll-section>
        <div
          className="cta-image-container"
          style={{ backgroundImage: `url('/assets/figma/a7ca385cea25f3635392fa96b7c02cf0a6d12cae.png')` }}
        />
      </section>

      {/* Instagram Gallery */}
      <InstagramSection />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default ExperiencesAtacama;